import React, { useLayoutEffect, useState } from 'react'
import { OverridableComponent } from 'interfaces/overridable-props'
import classNames from 'classnames'
import IconWrapper from './components/icon-wrapper/IconWrapper'
import ButtonProps from './resources/ButtonProps.interface'
import {
  ButtonVariant,
  ButtonSizes,
  ButtonType,
} from './resources/ButtonAttributes.enum'
import styles from './Button.module.scss'

export interface ButtonTypeMap<
  P = Record<any, any>,
  D extends React.ElementType = 'button'
> {
  props: P & ButtonProps
  defaultComponent: D
}

const Button: OverridableComponent<ButtonTypeMap> = ({
  type = ButtonType.Button,
  variant = ButtonVariant.Primary,
  size = ButtonSizes.Medium,
  ariaLabel = 'string',
  block = false,
  disabled = false,
  outline = false,
  color = null,
  opacity = null,
  children,
  className,
  onClick,
  icon,
  component: Component = 'button',
  fullWidth,
  ...props
}) => {
  const [buttonVariant, setButtonVariant] = useState('')
  const [buttonColor, setButtonColor] = useState('')
  const buttonSize = styles[`button${size}`]

  const buttonBlock = block ? styles[`buttonBlock`] : false

  // set color and opacity-brighness variant for className
  useLayoutEffect(() => {
    setButtonColor(styles[`button${color}`])
    const outlineTag = outline ? 'Outline' : ''
    if (color) {
      setButtonVariant('')
      setButtonColor(styles[`button${color}${outlineTag}`])
      if (opacity) {
        setButtonColor(styles[`button${color}${outlineTag}${opacity}`])
      }
    } else {
      setButtonVariant(styles[`button${variant}${outlineTag}`])
    }
  }, [variant, opacity, color, outline])

  return (
    <Component
      type={type}
      aria-label={ariaLabel}
      disabled={disabled}
      className={classNames(
        styles.button,
        buttonSize,
        buttonVariant,
        buttonBlock,
        buttonColor,
        className,
        fullWidth ? styles.fullWidth : ''
      )}
      onClick={onClick}
      {...props}
    >
      {icon && <IconWrapper icon={icon} sizes={size} />}
      {children}
    </Component>
  )
}

export default Button
export * from '../src/resources/ButtonAttributes.enum'
